import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { updateLastActivity } from "@/utils/sessionStorage";

// Verificar se as variáveis de ambiente estão configuradas
const checkEnvironmentVariables = (): { isValid: boolean; error?: string } => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || supabaseUrl === 'your_supabase_url_here') {
    return { isValid: false, error: 'VITE_SUPABASE_URL não está configurada' };
  }

  if (!supabaseAnonKey || supabaseAnonKey === 'your_supabase_anon_key_here') {
    return { isValid: false, error: 'VITE_SUPABASE_ANON_KEY não está configurada' };
  }

  return { isValid: true };
};

interface ConnectionConflict {
  conflict_account_name: string;
  conflict_account_id: string;
  owner_jid: string;
}

interface ConnectionStatus {
  hasInstance: boolean;
  isConnected: boolean;
  qrCode?: string;
  instanceName?: string;
  ownerNumber?: string;
  loading: boolean;
  error?: string;
  conflict?: ConnectionConflict;
}

const ConnectionTab: React.FC = () => {
  const { user } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    hasInstance: false,
    isConnected: false,
    loading: true
  });
  const [disconnecting, setDisconnecting] = useState(false);
  const [creatingInstance, setCreatingInstance] = useState(false);

  const createInstance = async () => {
    if (!user) return;

    // Verificar variáveis de ambiente antes de fazer requisições
    const envCheck = checkEnvironmentVariables();
    if (!envCheck.isValid) {
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: `Configuração inválida: ${envCheck.error}. Verifique o arquivo .env`
      });
      return;
    }

    setCreatingInstance(true);
    setConnectionStatus(prev => ({ ...prev, loading: true, error: undefined }));

    try {
      console.log('🚀 Creating instance for user:', user.id);
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-instance`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            account_id: user.account_id,
            name: user.name,
            email: user.email,
            phone: user.phone
          })
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Erro ao criar instância');
      }

      console.log('✅ Instance created successfully:', result.data);
      
      // Executar connect-instance imediatamente após sucesso
      console.log('🔄 Executing connect-instance after successful creation...');
      await executeConnectInstance();

    } catch (error) {
      console.error('❌ Erro ao criar instância:', error);
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro ao criar instância'
      });
    } finally {
      setCreatingInstance(false);
    }
  };

  const executeConnectInstance = async () => {
    if (!user) return;

    // Retry mechanism for race condition handling
    const maxRetries = 3;
    const baseDelay = 1000; // 1 second
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await executeConnectInstanceAttempt();
        return; // Success, exit retry loop
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        
        // Only retry for "No instance found" errors
        if (errorMessage.includes('No instance found for this account') && attempt < maxRetries) {
          console.log(`🔄 Retry attempt ${attempt}/${maxRetries} for connect-instance in ${baseDelay * attempt}ms...`);
          await new Promise(resolve => setTimeout(resolve, baseDelay * attempt));
          continue;
        }
        
        // If it's not a retryable error or we've exhausted retries, throw the error
        throw error;
      }
    }
  };

  const executeConnectInstanceAttempt = async () => {
    if (!user) return;

    // Verificar variáveis de ambiente antes de fazer requisições
    const envCheck = checkEnvironmentVariables();
    if (!envCheck.isValid) {
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: `Configuração inválida: ${envCheck.error}. Verifique o arquivo .env`
      });
      return;
    }

    try {
      console.log('🔌 Executing connect-instance for user:', user.id);
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/connect-instance?account_id=${user.account_id}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          }
        }
      );

      const result = await response.json();

      if (!result.success) {
        console.error('❌ Connect-instance failed:', result.error);
        if (result.error === 'whatsapp_already_connected') {
          setConnectionStatus({
            hasInstance: true,
            isConnected: false,
            loading: false,
            conflict: {
              conflict_account_name: result.conflict_account_name,
              conflict_account_id: result.conflict_account_id,
              owner_jid: result.owner_jid
            }
          });
          return;
        }
        setConnectionStatus({
          hasInstance: true,
          isConnected: false,
          loading: false,
          error: result.error || 'Erro ao verificar status da conexão'
        });
        return;
      }

      const { data } = result;
      console.log('📱 Connect-instance response:', data);

      // Check if already connected
      if (data.instance && data.instance.state === 'open') {
        console.log('✅ Instance is connected, full data:', JSON.stringify(data));
        const ownerJid = data.instance?.ownerJid
          || data.instance?.owner
          || data.instance?.wuid
          || data.instance?.profilePictureUrl
          || data?.ownerJid
          || '';
        const ownerNumber = ownerJid
          .replace(/@.*$/, '')
          .replace(/:\d+$/, '')
          .replace(/\D/g, '');
        console.log('📞 Owner number extracted:', { ownerJid, ownerNumber });
        setConnectionStatus({
          hasInstance: true,
          isConnected: true,
          instanceName: data.instance.instanceName,
          ownerNumber: ownerNumber || undefined,
          loading: false
        });
      } else if (data.base64) {
        console.log('📱 Instance needs connection - showing QR code');
        setConnectionStatus({
          hasInstance: true,
          isConnected: false,
          qrCode: data.base64,
          loading: false
        });
      } else {
        console.log('❓ Unknown connection status');
        setConnectionStatus({
          hasInstance: true,
          isConnected: false,
          loading: false,
          error: 'Status de conexão desconhecido'
        });
      }

    } catch (error) {
      console.error('❌ Erro ao executar connect-instance:', error);
      setConnectionStatus({
        hasInstance: true,
        isConnected: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro ao verificar conexão'
      });
    }
  };

  const checkConnectionStatus = async () => {
    if (!user) return;

    // Verificar variáveis de ambiente antes de fazer requisições
    const envCheck = checkEnvironmentVariables();
    if (!envCheck.isValid) {
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: `Configuração inválida: ${envCheck.error}. Verifique o arquivo .env`
      });
      return;
    }

    setConnectionStatus(prev => ({ ...prev, loading: true, error: undefined }));

    try {
      // Check if supabase is properly initialized
      if (!supabase) {
        throw new Error('Supabase não está configurado. Verifique as variáveis de ambiente VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY no arquivo .env');
      }

      console.log('🔍 Buscando instância para account_id:', user.account_id);
      console.log('📋 Dados do usuário completos:', {
        id: user.id,
        name: user.name,
        email: user.email,
        account_id: user.account_id
      });

      // Verificar auth do Supabase antes da query
      const { data: { session } } = await supabase.auth.getSession();
      console.log('🔐 Sessão do Supabase:', {
        has_session: !!session,
        user_id: session?.user?.id,
        expires_at: session?.expires_at
      });

      if (!session) {
        console.error('❌ Sessão do Supabase expirada ou inválida');
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }

      // Debug: Testar get_auth_account_id()
      console.log('🔍 Testando get_auth_account_id()...');
      const { data: debugData, error: debugError } = await supabase
        .rpc('debug_user_access');

      if (!debugError && debugData) {
        console.log('📊 Debug Info:', debugData);
      }

      // Check if user has instance
      const { data: instanceData, error: instanceError } = await supabase
        .from('account_instances')
        .select('*')
        .eq('account_id', user.account_id)
        .maybeSingle();

      console.log('📥 Resultado da query account_instances:', {
        data: instanceData,
        error: instanceError,
        account_id_usado: user.account_id
      });

      if (instanceError) {
        console.error('❌ Erro do Supabase:', instanceError);

        // Se for erro de RLS, tentar diagnosticar
        if (instanceError.code === 'PGRST116' || instanceError.message.includes('permission')) {
          const diagMessage = `
🔒 ERRO DE PERMISSÃO (RLS)

O banco de dados bloqueou o acesso à tabela account_instances.

Possíveis causas:
1. A função get_auth_account_id() está retornando NULL
2. Sua sessão Supabase expirou
3. As políticas RLS estão muito restritivas

Informações de Debug:
- Account ID: ${user.account_id}
- Auth User ID: ${session?.user?.id || 'N/A'}
- Sessão expira em: ${session?.expires_at ? new Date(session.expires_at * 1000).toLocaleString('pt-BR') : 'N/A'}
${debugData ? `\nDebug Info:\n${JSON.stringify(debugData, null, 2)}` : ''}

Ação recomendada: Clique em "Renovar Sessão" abaixo.

Erro original: ${instanceError.message}
Código: ${instanceError.code || 'N/A'}
Detalhes: ${instanceError.details || 'N/A'}`;

          throw new Error(diagMessage);
        }

        throw new Error(`Erro ao buscar instância no banco de dados: ${instanceError.message}. Código: ${instanceError.code || 'N/A'}. Detalhes: ${instanceError.details || 'N/A'}`);
      }

      if (!instanceData) {
        console.log('📭 Nenhuma instância encontrada para account_id:', user.account_id);
        console.log('🔧 Criando instância automaticamente...');

        // Criar instância automaticamente em vez de mostrar erro
        setConnectionStatus({
          hasInstance: false,
          isConnected: false,
          loading: true,
          error: undefined
        });

        // Chamar createInstance que já tem toda a lógica
        // (cria no banco + Evolution API + chama executeConnectInstance)
        await createInstance();
        return;
      }

      // Se tem instância, executar connect-instance
      console.log('✅ Instância encontrada:', {
        instance_name: instanceData.instance_name,
        instance_id: instanceData.instance_id,
        account_id: instanceData.account_id
      });
      await executeConnectInstance();

    } catch (error) {
      console.error('Erro ao verificar status da conexão:', error);
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    }
  };

  const handleDisconnect = async () => {
    if (!user) return;

    // Verificar variáveis de ambiente antes de fazer requisições
    const envCheck = checkEnvironmentVariables();
    if (!envCheck.isValid) {
      setConnectionStatus(prev => ({
        ...prev,
        error: `Configuração inválida: ${envCheck.error}. Verifique o arquivo .env`
      }));
      return;
    }

    setDisconnecting(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/disconnect-instance`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            account_id: user.account_id
          })
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Erro ao desconectar instância');
      }

      // Refresh connection status
      await checkConnectionStatus();

    } catch (error) {
      console.error('Erro ao desconectar:', error);
      setConnectionStatus(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Erro ao desconectar'
      }));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRefresh = () => {
    checkConnectionStatus();
  };

  const handleRenewSession = async () => {
    if (!user || !supabase) return;

    setConnectionStatus(prev => ({ ...prev, loading: true, error: undefined }));

    try {
      console.log('🔄 Renovando sessão Supabase...');

      // Forçar refresh do token
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) {
        throw new Error(`Erro ao renovar sessão: ${refreshError.message}`);
      }

      if (!session) {
        throw new Error('Não foi possível renovar a sessão. Por favor, faça login novamente.');
      }

      console.log('✅ Sessão renovada com sucesso');
      console.log('🔐 Nova sessão expira em:', new Date(session.expires_at! * 1000).toLocaleString('pt-BR'));

      // Se for agency_user, atualizar a sessão RLS também
      if (user.type === 'agency_user' && user.account_id && supabase) {
        console.log('🔄 Atualizando sessão RLS para agency_user...');
        const { error: rlsError } = await supabase.rpc('update_agency_user_session', {
          p_account_id: user.account_id
        });

        if (rlsError) {
          console.error('⚠️ Erro ao atualizar sessão RLS:', rlsError);
        } else {
          console.log('✅ Sessão RLS atualizada');
        }
      }

      // Tentar verificar conexão novamente
      await checkConnectionStatus();

    } catch (error) {
      console.error('❌ Erro ao renovar sessão:', error);
      setConnectionStatus({
        hasInstance: false,
        isConnected: false,
        loading: false,
        error: error instanceof Error ? error.message : 'Erro ao renovar sessão'
      });
    }
  };

  // Check status on component mount
  useEffect(() => {
    // Atualizar atividade do usuário
    updateLastActivity()
    checkConnectionStatus();
  }, [user]);

  // Auto-refresh every 10 seconds when showing QR code
  useEffect(() => {
    if (connectionStatus.qrCode && !connectionStatus.isConnected) {
      const interval = setInterval(checkConnectionStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [connectionStatus.qrCode, connectionStatus.isConnected]);

  if (connectionStatus.loading || creatingInstance) {
    return (
      <div className="flex flex-col items-center justify-center h-auto space-y-6 py-[15vh]">
        <div className="w-12 h-12 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
        <p className="text-neutral-50">
          {creatingInstance ? 'Criando instância WhatsApp...' : 'Verificando status da conexão...'}
        </p>
      </div>
    );
  }

  if (connectionStatus.error) {
    const isRLSError = connectionStatus.error.includes('ERRO DE PERMISSÃO') ||
                       connectionStatus.error.includes('RLS');

    return (
      <div className="flex flex-col items-center justify-center h-auto space-y-6 py-[15vh]">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="text-center max-w-2xl">
          <h3 className="text-lg font-semibold text-neutral-50 mb-2">Erro na Conexão</h3>
          <pre className="text-neutral-300 text-xs mb-4 text-left bg-neutral-900 p-4 rounded-lg overflow-auto max-h-[400px] font-mono whitespace-pre-wrap">
            {connectionStatus.error}
          </pre>
          <div className="flex gap-3 justify-center flex-wrap">
            {isRLSError && (
              <Button
                onClick={handleRenewSession}
                className="bg-blue-600 hover:bg-blue-700 text-white min-w-[140px]"
              >
                Renovar Sessão
              </Button>
            )}
            <Button onClick={handleRefresh} className="bg-[#E4E4E7] min-w-[120px]">
              Tentar Novamente
            </Button>
            <Button onClick={createInstance} variant="outline" className="min-w-[120px]" disabled={creatingInstance}>
              {creatingInstance ? 'Criando...' : 'Criar Instância'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (connectionStatus.isConnected) {
    const formatPhoneDisplay = (num: string) => {
      if (!num) return '';
      if (num.startsWith('55') && num.length >= 12) {
        const ddd = num.slice(2, 4);
        const rest = num.slice(4);
        if (rest.length === 9) return `+55 (${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
        if (rest.length === 8) return `+55 (${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
      }
      return `+${num}`;
    };

    return (
      <div className="flex flex-col items-center justify-center h-auto space-y-6 py-[15vh]">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="text-center max-w-md">
          <h3 className="text-lg font-semibold text-neutral-50 mb-2">WhatsApp Conectado</h3>
          {connectionStatus.ownerNumber ? (
            <p className="text-neutral-300 text-sm mb-2">
              O número de WhatsApp <span className="text-white font-semibold">{formatPhoneDisplay(connectionStatus.ownerNumber)}</span> está conectado ao WhatsApp.
            </p>
          ) : (
            <p className="text-neutral-300 text-sm mb-2">
              Seu WhatsApp está conectado.
            </p>
          )}
          <p className="text-amber-400/80 text-xs mb-4">
            Obs: Se o aparelho ficar desconectado por 14 dias, será desconectado automaticamente.
          </p>
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={handleDisconnect} 
              variant="destructive"
              disabled={disconnecting}
              className="min-w-[120px]"
            >
              {disconnecting ? 'Desconectando...' : 'Desconectar'}
            </Button>
            <Button onClick={handleRefresh} variant="outline" className="min-w-[120px] border-white text-white hover:bg-white hover:text-black">
              Atualizar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (connectionStatus.conflict) {
    const conflictPhone = connectionStatus.conflict.owner_jid
      .replace(/@.*$/, '')
      .replace(/:\d+$/, '')
      .replace(/\D/g, '');

    const formatConflictPhone = (num: string) => {
      if (!num) return '';
      if (num.startsWith('55') && num.length >= 12) {
        const ddd = num.slice(2, 4);
        const rest = num.slice(4);
        if (rest.length === 9) return `+55 (${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
        if (rest.length === 8) return `+55 (${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
      }
      return `+${num}`;
    };

    return (
      <div className="flex flex-col items-center justify-center h-auto space-y-6 py-[15vh]">
        <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className="text-center max-w-lg">
          <h3 className="text-lg font-semibold text-neutral-50 mb-3">Numero ja conectado em outra conta</h3>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
            <p className="text-neutral-200 text-sm mb-2">
              O numero <span className="text-white font-semibold">{formatConflictPhone(conflictPhone)}</span> ja esta conectado na conta:
            </p>
            <p className="text-amber-400 font-semibold text-base">
              {connectionStatus.conflict.conflict_account_name}
            </p>
          </div>
          <p className="text-neutral-400 text-xs mb-4">
            Cada numero de WhatsApp so pode estar conectado em uma unica conta. Desconecte o numero na outra conta antes de conectar aqui.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={handleRefresh} className="bg-[#E4E4E7] min-w-[140px]">
              Tentar Novamente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (connectionStatus.qrCode) {
    return (
      <div className="flex flex-col items-center justify-center h-auto space-y-6 py-[10vh]">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-neutral-50 mb-2">Conectar WhatsApp</h3>
          <p className="text-neutral-300 text-sm">
            Escaneie o QR Code com seu WhatsApp para conectar
          </p>
        </div>
        
        <div className="bg-white p-4 rounded-lg">
          <img 
            src={connectionStatus.qrCode} 
            alt="QR Code para conectar WhatsApp" 
            className="w-[250px] h-[250px] object-contain"
          />
        </div>
        
        <div className="text-center">
          <p className="text-neutral-400 text-xs mb-4">
            O QR Code será atualizado automaticamente a cada 30 segundos ou{' '}
            <button 
              onClick={handleRefresh}
              className="text-blue-400 hover:text-blue-300 underline cursor-pointer"
            >
              Atualizar
            </button>
            {' '}agora
          </p>
          <Button onClick={handleRefresh} variant="outline" className="min-w-[140px]">
            Atualizar Agora
          </Button>
        </div>
      </div>
    );
  }

  // Este estado não deveria acontecer mais, pois criamos automaticamente
  return (
    <div className="flex flex-col items-center justify-center h-auto space-y-6 py-[15vh]">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-neutral-50 mb-2">Criando Instância</h3>
        <p className="text-neutral-300 text-sm mb-4">
          Aguarde enquanto criamos sua instância WhatsApp...
        </p>
        <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin mx-auto"></div>
      </div>
    </div>
  );
};

export default ConnectionTab;